import React, { useCallback, useEffect, useState } from 'react';
import { batch, useSelector } from 'react-redux';
import {
  changeDateAction,
  changeViewAction,
  clearIncidentsAction,
  loadIncidentsAction,
  resetCalendarAction,
} from '../../../redux/actions/incidents';
import { useQuery } from '@apollo/client';
import GET_CALENDAR_INCIDENTS from '../apollo/GET_CALENDAR_INCIDENTS';
import Toolbar from './Toolbar';
import { IncidentCalendarWrapper } from './IncidentWrapper';
import DateCellWrapper from './DateCellWrapper';
import { IncidentDeleteModal } from '../../../components/Incidents/IncidentModals';
import { CalendarComponent, IncidentTypesLegend } from './CalendarUI';
import { ViewsEnum } from '../utils/constants';
import { momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import { IIncident } from '../../../utils/apollo/graphqlSchema';
import { isEmpty, isNil } from 'lodash';
import { MaybeNil } from '../../../utils/interfaces';
import { IncidentType } from '../../../components/IncidentType';
import { RootState } from '../../../redux/store';
import { selectOpenedModalType } from '../../../redux/selectors/modal';
import {
  closeModal,
  ModalSliceType,
  openModal,
} from '../../../redux/slices/modal';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import useFormulaConfigWeekStartEffect from '../../Formulas/utils/useFormulaConfigWeekStartEffect';
import { useAnalytics } from '../../../utils/analytics/useAnalytics';
import { pageViewed } from '../../../utils/analytics/events/page/events';
import styled from 'styled-components';

const EVENT_DATE_FORMAT = 'YYYY-MM-DD';

interface IReactBigCalendarIncident extends IIncident {
  startAsDate: MaybeNil<Date>;
  endAsDate: MaybeNil<Date>;
}

type DateType = 'start' | 'end';

const convertToCalendarDate = (
  dateAsStr: MaybeNil<string>,
  dateType: DateType,
) => {
  if (isNil(dateAsStr)) {
    return undefined;
  }

  const momentDate = moment(dateAsStr, EVENT_DATE_FORMAT);

  if (dateType === 'end') {
    return momentDate.endOf('day').toDate();
  } else {
    return momentDate.startOf('day').toDate();
  }
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 24px;
`;

export default function Calendar() {
  const dispatch = useAppDispatch();
  const { track } = useAnalytics();
  useEffect(() => {
    track(
      pageViewed({
        category: 'incidents',
        name: 'calendar',
        id: null,
      }),
    );
  }, [track]);

  const [localizer, setLocalizer] = useState(momentLocalizer(moment));
  const { loading: formulaConfigLoading } = useFormulaConfigWeekStartEffect();

  useEffect(() => {
    if (!formulaConfigLoading) {
      const newLocalizer = momentLocalizer(moment);
      setLocalizer(newLocalizer);
    }
  }, [formulaConfigLoading]);

  /* Fetch all calendar upon loading and populate them in store */
  const onIncidentsFetch = useCallback(
    ({ incidents }: { incidents: IIncident[] }) => {
      dispatch(loadIncidentsAction({ incidents }));
    },
    [dispatch],
  );
  useQuery(GET_CALENDAR_INCIDENTS, {
    onCompleted: onIncidentsFetch,
  });

  /* Current date value of the calendar */
  const date = useSelector(
    (state: RootState) => state?.incidents?.calendar?.date,
  );
  /* Current view of the calendar (default is month) */
  const view = useSelector(
    (state: RootState) => state?.incidents?.calendar?.view,
  );
  /* Calendar incidents from store */
  const incidents = useSelector(
    (state: RootState) => state?.incidents?.incidents,
  ) as IIncident[];
  /* Incident types from store */
  const incidentTypes = useSelector(
    (state: RootState) => state?.incidents?.incidentTypes,
  );

  /* Available calendar views (month only for now) */
  const allViews = Object.values(ViewsEnum).map(
    (singleView: ViewsEnum) => singleView,
  );

  const onNavigate = useCallback(
    (newDate: Date) => {
      batch(() => {
        dispatch(changeDateAction(newDate));
        /* Close the show more modal */
        dispatch(closeModal());
      });
    },
    [dispatch],
  );

  const onView = useCallback(
    (newView: ViewsEnum) => {
      dispatch(changeViewAction(newView));
    },
    [dispatch],
  );

  const toggleShowMoreModal = useCallback(
    (currentIncidents: any, currentDate: any) => {
      dispatch(
        openModal({
          type: ModalSliceType.IncidentShowMore,
          data: {
            incidents: currentIncidents,
            date: currentDate,
          },
        }),
      );
    },
    [dispatch],
  );

  const showMore = useCallback(
    (total: any) => <>{`+${total} more incidents`}</>,
    [],
  );

  const isDeleteModalOpen = useAppSelector((state) =>
    selectOpenedModalType(state, ModalSliceType.IncidentDelete, ''),
  );

  const disableOnDrillDown = () => null;

  useEffect(() => {
    return () => {
      batch(() => {
        dispatch(clearIncidentsAction());
        dispatch(resetCalendarAction());
      });
    };
  }, [dispatch]);

  if (!date) {
    return <h2>Loading...</h2>;
  }

  // > Big Calendar is all about dates and times. Probably one of the easiest things that beginners miss,
  // > when first implementing Big Calendar, is that we require true JS Date objects in our props and event definitions.
  // https://jquense.github.io/react-big-calendar/examples/index.html?path=/docs/guides-understanding-dates--page
  const reactCalendarEvents = incidents.map<IReactBigCalendarIncident>(
    (incident: IIncident) => ({
      ...incident,
      startAsDate: convertToCalendarDate(incident.start, 'start'),
      endAsDate: convertToCalendarDate(incident.end, 'end'),
    }),
  );

  return (
    <Container>
      <CalendarComponent
        view={view}
        views={allViews}
        localizer={localizer}
        date={date}
        events={reactCalendarEvents}
        startAccessor="startAsDate"
        endAccessor="endAsDate"
        components={{
          toolbar: Toolbar,
          dateCellWrapper: DateCellWrapper,
          eventWrapper: IncidentCalendarWrapper,
        }}
        onNavigate={onNavigate}
        onView={onView}
        onShowMore={toggleShowMoreModal}
        messages={{
          showMore,
        }}
        onDrillDown={disableOnDrillDown}
      />
      {!isEmpty(incidentTypes) && (
        <IncidentTypesLegend>
          {Object.values(incidentTypes).map(({ id, name }) => (
            <IncidentType key={id} name={name} />
          ))}
        </IncidentTypesLegend>
      )}
      {isDeleteModalOpen && <IncidentDeleteModal />}
    </Container>
  );
}
